import { useMemo, useRef, useState } from 'react';
import { sanitizeEmailHtml } from '../../utils/htmlSanitizer';

interface MessageBodyProps {
  html?: string;
  text?: string;
  useSenderFormatting?: boolean;
}

/**
 * Default CSS injected into the sandbox iframe so naive emails still look
 * reasonable (rendering with raw browser defaults produces a serif soup on
 * a white page). Sender stylesheets still override these because they're
 * emitted later in the cascade.
 */
const IFRAME_DEFAULT_CSS = `
  body {
    margin: 0;
    padding: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
      'Helvetica Neue', Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #222;
    background: transparent;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  a { color: #2563eb; text-decoration: underline; }
  img { max-width: 100%; height: auto; }
  table { max-width: 100%; }
  * { box-sizing: border-box; }
  @media print {
    html,
    body {
      height: auto !important;
      min-height: 0 !important;
      overflow: visible !important;
      scrollbar-width: none !important;
    }
    html::-webkit-scrollbar,
    body::-webkit-scrollbar,
    *::-webkit-scrollbar {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
    }
    * {
      overflow: visible !important;
      scrollbar-width: none !important;
    }
  }
`;

function shouldPreserveRichEmailFormatting(content: string, hasMarkup: boolean): boolean {
  if (!hasMarkup) return false;

  const inlineStyleCount = (content.match(/\sstyle\s*=/gi) || []).length;
  const tableCount = (content.match(/<table\b/gi) || []).length;
  const hasEmbeddedVisuals = /<(?:img|picture|svg)\b/i.test(content);
  const hasFullHtmlShell = /<(?:html|head|body)\b/i.test(content);
  const hasEmailStylesheet = /<style\b|@media\b/i.test(content);
  const hasLayoutAttributes = /\s(?:bgcolor|background|cellpadding|cellspacing|width|height|align|valign)=/i.test(content);

  return (
    hasEmbeddedVisuals ||
    (tableCount > 0 && (hasLayoutAttributes || hasEmailStylesheet || hasFullHtmlShell || inlineStyleCount >= 3)) ||
    inlineStyleCount >= 12
  );
}

/**
 * Safe HTML renderer for email body content.
 *
 * Two render paths:
 *
 *  - **Native text-like mail** → `<style>` blocks stripped, sanitized HTML
 *    rendered inline so theme colors apply and simple emails feel native.
 *
 *  - **Sender/rich formatting** → sanitized HTML (style blocks preserved)
 *    rendered inside a sandboxed iframe via `srcdoc`. This is used when the
 *    user turns on sender formatting or when the message clearly has a
 *    designed layout (images, layout tables, email CSS). This is the standard
 *    email-client pattern (Gmail, Outlook Web). Without isolation, sender CSS
 *    leaks into the host document and can:
 *    override global selectors (`body`, `*`, `table`), define
 *    `@keyframes` or `@font-face` that cause the entire app to flicker
 *    or reflow, and collide with our theme tokens. The iframe prevents
 *    all of that with no functionality loss.
 */
export default function MessageBody({ html = '', text = '', useSenderFormatting = false }: MessageBodyProps) {
  const content = html || text;
  const hasMarkup = /<\/?[a-z][\s\S]*>/i.test(content);
  const preserveSenderFormatting = useSenderFormatting || shouldPreserveRichEmailFormatting(content, hasMarkup);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [iframeHeight, setIframeHeight] = useState(200);

  // Sanitized HTML for the inline (non-sender-formatting) path. Style
  // blocks are stripped so they cannot affect the host document.
  const inlineSanitized = useMemo(() => {
    if (!content || !hasMarkup || preserveSenderFormatting) return '';
    return sanitizeEmailHtml(
      content.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ''),
    );
  }, [content, hasMarkup, preserveSenderFormatting]);

  // Browsers commonly print sandboxed iframes as one fixed-height replaced
  // element, which clips long sender-formatted emails instead of paginating.
  // This print-only host-DOM copy strips sender style blocks so the message
  // can flow across pages under EdgeMail's print stylesheet.
  const printSanitized = useMemo(() => {
    if (!content || !hasMarkup || !preserveSenderFormatting) return '';
    return sanitizeEmailHtml(
      content.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ''),
    );
  }, [content, hasMarkup, preserveSenderFormatting]);

  // Full srcdoc string for the iframe path. Style blocks preserved
  // because the iframe boundary isolates them from the host.
  const iframeSrcDoc = useMemo(() => {
    if (!content || !hasMarkup || !preserveSenderFormatting) return '';
    const sanitized = sanitizeEmailHtml(content);
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<base target="_blank">
<style>${IFRAME_DEFAULT_CSS}</style>
</head>
<body>${sanitized}</body>
</html>`;
  }, [content, hasMarkup, preserveSenderFormatting]);

  const measureIframe = () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const next = Math.max(
      doc.body?.scrollHeight ?? 0,
      doc.documentElement?.scrollHeight ?? 0,
    );
    if (next > 0) setIframeHeight(next + 8);
  };

  const handleIframeLoad = () => {
    measureIframe();
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    // Late-loading images/fonts change content height after the initial
    // `load` fires. Re-measure when each settles so the iframe grows to
    // fit without scrolling.
    Array.from(doc.images).forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', measureIframe, { once: true });
        img.addEventListener('error', measureIframe, { once: true });
      }
    });

    const fonts = (doc as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
    fonts?.ready?.then(measureIframe).catch(() => {});
  };

  if (!content) return null;

  const plainClass = 'edgemail-message-body-native em-text-sm text-[var(--em-text-secondary)] leading-relaxed max-w-none';
  const wrapperClass = preserveSenderFormatting
    ? 'em-text-sm max-w-none bg-white text-black rounded-xl p-4 shadow-inner'
    : plainClass;

  if (!hasMarkup) {
    return (
      <div className={`${plainClass} whitespace-pre-wrap break-words`}>
        {content}
      </div>
    );
  }

  if (preserveSenderFormatting) {
    return (
      <div className={wrapperClass}>
        <iframe
          ref={iframeRef}
          data-mail-body-iframe
          srcDoc={iframeSrcDoc}
          onLoad={handleIframeLoad}
          title="Message body"
          // allow-same-origin is required to measure body scrollHeight;
          // omitting allow-scripts blocks any JS the email may try to
          // run. allow-popups lets links open in new tabs.
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="no-referrer"
          style={{
            width: '100%',
            height: iframeHeight,
            border: 0,
            background: 'transparent',
            display: 'block',
          }}
        />
        {printSanitized && (
          <div
            className="em-print-only em-print-body-fallback"
            dangerouslySetInnerHTML={{ __html: printSanitized }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`${wrapperClass} [&_a]:text-[var(--em-accent-primary)] [&_a]:underline [&_img]:max-w-full [&_img]:h-auto`}
      dangerouslySetInnerHTML={{ __html: inlineSanitized }}
    />
  );
}
